import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getSilenceCheck,
  getTrackingSettings,
  saveTrackingSettings,
  setAutoMarking,
  setSilenceCheck,
} from "../../../services/tripTrackingApi";
import { formatDateTimeDMY } from "../../../utils/dateFormat";
import { PLACE_CIRCLE_MAX, PLACE_CIRCLE_MIN } from "../../../utils/placeCircle";

// The page's number fields, in the order shown. Hours are picked from a list instead.
const MARKING_FIELDS = [
  { key: "arrivalRadiusM", hint: "Within this distance of a place counts as arriving." },
  { key: "leaveRadiusM", hint: "Beyond this distance the place counts as left." },
  { key: "dwellMinutes", hint: "How long inside the arrival circle before it counts as Reached, so driving past does not." },
  { key: "stopRadiusM", hint: "An unplanned stop is the vehicle staying within this area, away from every plan place." },
  { key: "stopMinutes", hint: "How long it must stay there to show as an unplanned stop (office view only)." },
];
const ALERT_FIELDS = [
  { key: "silenceMinutes", hint: "No location from the driver's phone this long on a running trip emails the office." },
  { key: "silenceRemindMinutes", hint: "One reminder email this long after the first, if location is still missing." },
];
const ADVANCED_FIELDS = [
  { key: "maxAccuracyM", hint: "GPS points rougher than this are left out of marking and the route." },
];
const HOUR_FIELDS = ["nightStayHour", "quietFromHour", "quietToHour"];
const VALUE_KEYS = [...MARKING_FIELDS, ...ALERT_FIELDS, ...ADVANCED_FIELDS].map((field) => field.key).concat(HOUR_FIELDS);

const UNIT_TEXT = { m: "m", min: "min" };

function hourLabel(hour) {
  const h = Number(hour);
  if (!Number.isFinite(h)) return "";
  const suffix = h < 12 ? "AM" : "PM";
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve} ${suffix}`;
}

function toForm(saved) {
  const form = {};
  VALUE_KEYS.forEach((key) => { form[key] = saved?.[key] ?? ""; });
  form.alertRecipients = saved?.alertRecipients || "";
  return form;
}

function toRequest(form) {
  const request = {};
  VALUE_KEYS.forEach((key) => {
    const value = String(form[key] ?? "").trim();
    request[key] = value === "" ? null : Math.round(Number(value));
  });
  request.alertRecipients = form.alertRecipients.trim() || null;
  return request;
}

function outOfLimits(value, limit) {
  if (value === "" || value == null || !limit) return false;
  const number = Number(value);
  return !Number.isFinite(number) || number < limit.min || number > limit.max;
}

/**
 * Tracking Settings: the circles trip places are marked by, when the office is alerted about a
 * phone that stopped sending, who is emailed, and the two on/off switches. Empty fields use the
 * default. Changes apply to running trips from their next location point.
 */
export default function TrackingSettingsManager() {
  const [settings, setSettings] = useState(null);
  const [silenceCheck, setSilenceCheckState] = useState(null);
  const [form, setForm] = useState(toForm(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [switchBusy, setSwitchBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, check] = await Promise.all([getTrackingSettings(), getSilenceCheck().catch(() => null)]);
      setSettings(data);
      setSilenceCheckState(check);
      setForm(toForm(data.saved));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load the tracking settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const limits = settings?.limits || {};
  const defaults = settings?.defaults || {};
  const dirty = useMemo(
    () => settings && JSON.stringify(toRequest(form)) !== JSON.stringify(toRequest(toForm(settings.saved))),
    [form, settings],
  );
  const invalid = [...MARKING_FIELDS, ...ALERT_FIELDS, ...ADVANCED_FIELDS].some((field) => outOfLimits(form[field.key], limits[field.key]));

  const update = (key, value) => setForm((old) => ({ ...old, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const data = await saveTrackingSettings(toRequest(form));
      setSettings(data);
      setForm(toForm(data.saved));
      toast.success("Tracking settings saved. Running trips use them from their next location point.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save the tracking settings");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setForm(toForm(null));
    toast.info("All fields are back to their defaults. Click Save to apply.");
  };

  const changeAutoMarking = async (on) => {
    const question = on
      ? "Turn on automatic marking?\n\nReached and Left will be marked from the driver's location on every running trip. A driver's own tap is never changed."
      : "Turn off automatic marking?\n\nPlaces, pickup and days will no longer be marked from the location. Marks already made stay.";
    if (!window.confirm(question)) return;
    setSwitchBusy("auto");
    try {
      setSettings(await setAutoMarking(on));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not change automatic marking");
    } finally {
      setSwitchBusy(null);
    }
  };

  const changeSilenceCheck = async (on) => {
    const question = on
      ? "Turn on location alerts for all trips?\n\nThe office is emailed, and Manage Bookings beeps, when a running trip has no location for the time set below."
      : "Turn off location alerts for all trips?\n\nNo emails and no alarms until someone turns them back on. Open alerts are cleared.";
    if (!window.confirm(question)) return;
    setSwitchBusy("silence");
    try {
      setSilenceCheckState(await setSilenceCheck(on));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not change the location alerts");
    } finally {
      setSwitchBusy(null);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="text-center py-5"><Spinner /></Card.Body>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="text-center py-5">
          <div className="mb-3">The tracking settings could not be loaded.</div>
          <Button size="sm" onClick={load}>Try again</Button>
        </Card.Body>
      </Card>
    );
  }

  const numberField = (field) => {
    const limit = limits[field.key] || {};
    const unit = UNIT_TEXT[limit.unit] || limit.unit;
    const bad = outOfLimits(form[field.key], limit);
    return (
      <Col md={6} lg={4} key={field.key}>
        <Form.Label className="fw-semibold mb-1">{limit.label}</Form.Label>
        <InputGroup hasValidation>
          <Form.Control
            type="number"
            min={limit.min}
            max={limit.max}
            value={form[field.key]}
            placeholder={String(defaults[field.key] ?? "")}
            isInvalid={bad}
            disabled={saving}
            onChange={(event) => update(field.key, event.target.value)}
          />
          <InputGroup.Text>{unit}</InputGroup.Text>
          <Form.Control.Feedback type="invalid">
            Between {limit.min} and {limit.max} {unit}.
          </Form.Control.Feedback>
        </InputGroup>
        <div className="small text-muted mt-1">
          {field.hint} Default {defaults[field.key]} {unit}.
        </div>
      </Col>
    );
  };

  const hourField = (key, hint) => {
    const limit = limits[key] || { min: 0, max: 23 };
    const hours = [];
    for (let hour = limit.min; hour <= limit.max; hour += 1) hours.push(hour);
    return (
      <Col md={6} lg={4} key={key}>
        <Form.Label className="fw-semibold mb-1">{limit.label}</Form.Label>
        <Form.Select value={form[key]} disabled={saving} onChange={(event) => update(key, event.target.value)}>
          <option value="">Default ({hourLabel(defaults[key])})</option>
          {hours.map((hour) => <option key={hour} value={hour}>{hourLabel(hour)}</option>)}
        </Form.Select>
        {hint ? <div className="small text-muted mt-1">{hint}</div> : null}
      </Col>
    );
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">Tracking Settings</div>
          <div className="small text-muted">
            How trips are marked from the driver's location, and when the office is alerted. Empty fields use the default.
            Changes apply to running trips from their next location point; completed trips keep their marks.
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={resetToDefaults} disabled={saving}>Use defaults</Button>
          <Button variant="primary" onClick={save} disabled={saving || invalid || !dirty}>
            {saving ? <><Spinner size="sm" className="me-2" />Saving</> : "Save"}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="fw-semibold mb-2">On / off</div>
        <Row className="g-3">
          <Col md={6}>
            <ServerSwitchCard
              id="tracking-silence-check"
              label="Location alerts (all trips)"
              hint="Emails the office and beeps on Manage Bookings when a running trip stops sending location."
              state={silenceCheck}
              busy={switchBusy === "silence"}
              onChange={changeSilenceCheck}
            />
          </Col>
          <Col md={6}>
            <ServerSwitchCard
              id="tracking-auto-marking"
              label="Automatic marking"
              hint="Marks Reached and Left at each place, pickup and day from the driver's location."
              state={settings.autoMarking}
              busy={switchBusy === "auto"}
              onChange={changeAutoMarking}
            />
          </Col>
        </Row>

        <hr className="my-4" />
        <div className="fw-semibold mb-1">Place marking</div>
        <div className="small text-muted mb-3">
          A single place can have its own circle: set "Circle m" next to it in the trip's day plan, or in a booking's own
          plan ({PLACE_CIRCLE_MIN}-{PLACE_CIRCLE_MAX} m). Its leaving circle grows in the same proportion.
        </div>
        <Row className="g-3">
          {MARKING_FIELDS.map(numberField)}
          {hourField("nightStayHour", "A stay counts as the night's hotel, ending the day, once the vehicle is still there at this hour.")}
        </Row>

        <hr className="my-4" />
        <div className="fw-semibold mb-3">Location alerts</div>
        <Row className="g-3">
          {ALERT_FIELDS.map(numberField)}
          <Col md={6} lg={4}>
            <Form.Label className="fw-semibold mb-1">Send alert emails to</Form.Label>
            <Form.Control
              value={form.alertRecipients}
              placeholder="Booking alert email (default)"
              disabled={saving}
              onChange={(event) => update("alertRecipients", event.target.value)}
            />
            <div className="small text-muted mt-1">Up to 5 addresses, separated by commas.</div>
          </Col>
          {hourField("quietFromHour", null)}
          {hourField("quietToHour", "No new alerts or reminders between these hours. The same hour twice means no quiet hours.")}
        </Row>

        <details className="mt-4">
          <summary className="fw-semibold">Advanced</summary>
          <Row className="g-3 mt-1">
            {ADVANCED_FIELDS.map(numberField)}
          </Row>
        </details>

        <div className="small text-muted mt-4">
          {settings.updatedBy
            ? `Last saved by ${settings.updatedBy} on ${formatDateTimeDMY(settings.updatedAtIst)}.`
            : "Nothing saved yet: every value is at its default."}
          {dirty ? " You have unsaved changes." : ""}
        </div>
      </Card.Body>
    </Card>
  );
}

/** One office switch: who turned it on or off and when, and a note if it runs on another server. */
function ServerSwitchCard({ id, label, hint, state, busy, onChange }) {
  if (!state) {
    return (
      <div className="border rounded p-3 h-100">
        <Form.Check type="switch" id={id} label={label} checked={false} disabled readOnly />
        <div className="small text-muted mt-1">Could not load it - refresh to try again.</div>
      </div>
    );
  }
  const elsewhere = state.on && state.runsOn && state.runsOn !== state.thisServer;
  return (
    <div className="border rounded p-3 h-100">
      <Form.Check
        type="switch"
        id={id}
        label={<span className="fw-semibold">{label}</span>}
        checked={Boolean(state.on)}
        disabled={busy}
        onChange={(event) => onChange(event.target.checked)}
      />
      <div className="small text-muted mt-1">{hint}</div>
      <div className="small mt-1">
        <strong>{state.on ? "On" : "Off"}</strong>
        {state.changedBy
          ? ` - by ${state.changedBy}, ${formatDateTimeDMY(state.changedAtIst)}`
          : state.on ? " - set on the server" : ""}
      </div>
      {elsewhere ? (
        <div className="small fw-semibold text-danger mt-1">
          Runs on {state.runsOn}, not this server - switch it off and on again to move it here.
        </div>
      ) : null}
    </div>
  );
}
